import { action, computed, makeObservable, observable } from "mobx";
import { Entity } from "./entity";

type IndexDefs<T> = {[name: string]: (entity: T) => string};

export class EntityRepository<EntityInterface, E extends Entity<EntityInterface> = Entity<EntityInterface>> {
    readonly primaryKey: string;
    protected byPrimaryKey: {[key: string]: E};
    byIndex: {[name: string]: {[key: string]: E[]}};
    isFrozen: boolean = false;
    timeDiff: number = 0;
    private indexDefs: IndexDefs<EntityInterface>;
    private EntityClass: new (primaryKey: string, localValue: EntityInterface | null, remoteValue: EntityInterface | null, isFrozen?: boolean) => E;

    constructor(primaryKey: string, indexDefs?: IndexDefs<EntityInterface>, EntityClass?: new (...args: any[]) => E) {
        this.primaryKey = primaryKey;
        this.indexDefs = indexDefs || {};
        this.byPrimaryKey = {};
        this.byIndex = {};
        this.EntityClass = (EntityClass || Entity) as any;
        makeObservable<this, "byPrimaryKey">(this, {
            isFrozen: observable,
            byPrimaryKey: observable.deep,
            byIndex: observable.deep,
            all: computed,
            active: computed,
            changed: computed,
            addRemote: action,
            save: action,
            delete: action,
            freeze: action,
            reset: action,
            clear: action
        });
        this.clear();
    }

    setTimeDiff(value: number) {
        this.timeDiff = value;
    }

    get all(): E[] {
        return Object.values(this.byPrimaryKey).filter(x => !x.isFuture) as E[];
    }

    get active(): E[] {
        return this.all.filter(x => !x.isDeleted);
    }

    get changed(): E[] {
        return this.all.filter(x => x.isChanged);
    }

    addRemote(entity: EntityInterface): E {
        const pk = (entity as any)[this.primaryKey] as string;
        let existingEntity = this.byPrimaryKey[pk];

        if (existingEntity !== undefined && existingEntity.remoteValue !== null) {
            for (const [name, keyFunc] of Object.entries(this.indexDefs)) {
                const oldKey = keyFunc(existingEntity.remoteValue);
                const arr = this.byIndex[name][oldKey];
                if (arr) {
                    const i = arr.indexOf(existingEntity);
                    if (i !== -1) arr.splice(i, 1);
                    if (arr.length === 0) delete this.byIndex[name][oldKey];
                }
            }
        }

        if (existingEntity === undefined) {
            existingEntity = new this.EntityClass(this.primaryKey, null, entity, this.isFrozen);
            this.byPrimaryKey[pk] = existingEntity;
        }
        existingEntity.updateRemote(entity, this.isFrozen);

        for (const [name, keyFunc] of Object.entries(this.indexDefs)) {
            const newKey = keyFunc(entity);
            if (!this.byIndex[name][newKey]) this.byIndex[name][newKey] = [];
            this.byIndex[name][newKey].push(existingEntity);
        }

        return existingEntity;
    }

    getByIndex(indexName: string, key: string): E[] {
        return this.byIndex[indexName]?.[key] ?? [];
    }

    get(primaryKey: string): E | null {
        return this.byPrimaryKey[primaryKey] || null;
    }

    save(entity: E): E {
        const primaryKey = (entity as any)[this.primaryKey] as string;
        let stored = this.byPrimaryKey[primaryKey];
        if (stored === undefined) {
            stored = new this.EntityClass(this.primaryKey, entity.localValue, entity.remoteValue);
            this.byPrimaryKey[primaryKey] = stored;
        }
        if (entity.localValue) {
            stored.update(entity.localValue);
        }
        return stored;
    }

    delete(primaryKey: string): void {
        const entity = this.byPrimaryKey[primaryKey];
        if (entity) {
            entity.delete();
        }
    }

    freeze() {
        this.isFrozen = true;
    }

    reset() {
        for (let entity of this.all.filter(x => x.isChanged || x.isPending)) {
            entity.revert();
        }
        for (const [key, entity] of Object.entries(this.byPrimaryKey)) {
            if (entity.isOrphan) {
                delete this.byPrimaryKey[key]
            }
        }
        this.isFrozen = false;
    }

    clear() {
        this.byPrimaryKey = {};
        this.byIndex = Object.fromEntries(Object.keys(this.indexDefs).map(name => [name, {}]));
        this.isFrozen = false;
    }
}
